import React, { createContext, useContext, useCallback, useRef } from "react";
import { fetchAuthSession } from "@aws-amplify/auth";
import { useAuth } from "./AuthContext";

const API_ENDPOINT = process.env.NODE_ENV === "production" ? "https://api.executable.app" : "https://dev.executable.app";

const ApiContext = createContext({});

export const ApiProvider = ({ children }) => {
	const { isAuthenticated } = useAuth();
	const pendingRequests = useRef(new Map());
	/**
	 * API client for making RESTful requests
	 *
	 * IMPORTANT REST PRINCIPLES:
	 * - GET requests: Can include IDs in the URL path (e.g. /app?id={id})
	 * - All other methods (PUT, POST, DELETE):
	 *   - Must NOT append IDs or additional paths to the URL
	 *   - Must send all data in the request body
	 *   - Base URL should be used as is (e.g. /app)
	 *
	 * Example:
	 * ✅ GET /app?id={id} - Correct for retrieving data
	 * ✅ PUT /app - Correct, with data in body
	 * ❌ PUT /app/{id}/services - Incorrect, don't append to URL
	 * ❌ POST /app/{id}/services - Incorrect, don't append to URL
	 */
	const api = useCallback(
		async (path, options = {}) => {
			if (!isAuthenticated) {
				throw new Error("User is not authenticated");
			}

			// Create a unique key for this request
			const requestKey = `${options.method || "GET"}:${path}:${JSON.stringify(options.body || {})}`;

			// Check if there's a pending request with the same key
			const existingRequest = pendingRequests.current.get(requestKey);
			if (existingRequest) {
				return existingRequest;
			}

			const { tokens } = await fetchAuthSession();
			const { body, ...restOptions } = options;

			// Create the promise for this request
			const requestPromise = (async () => {
				try {
					const response = await fetch(`${API_ENDPOINT}${path}`, {
						...restOptions,
						method: options.method || "GET",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${tokens.accessToken.toString()}`,
							...options.headers,
						},
						...(body && { body: JSON.stringify(body) }),
					});

					if (!response.ok) {
						console.log("API Request:", {
							url: `${API_ENDPOINT}${path}`,
							method: options.method || "GET",
							body,
						});
						let errorData;
						try {
							errorData = await response.json();
							console.error("API Error Response:", errorData);
						} catch (e) {
							console.error("Failed to parse error response:", e);
							throw new Error(`HTTP error! status: ${response.status}`);
						}
						throw new Error(errorData.message || `API request failed with status: ${response.status}`);
					}

					// For 204 No Content responses, don't try to parse JSON
					if (response.status === 204) {
						return null;
					}

					try {
						const data = await response.json();
						return data;
					} catch (e) {
						console.log("API Request:", {
							url: `${API_ENDPOINT}${path}`,
							method: options.method || "GET",
							body,
						});
						console.error("Failed to parse success response:", e);
						throw new Error("Invalid response format from server");
					}
				} finally {
					// Remove this request from pending after a short delay
					setTimeout(() => {
						pendingRequests.current.delete(requestKey);
					}, 2500);
				}
			})();

			// Store the promise in the pending requests map
			pendingRequests.current.set(requestKey, requestPromise);

			return requestPromise;
		},
		[isAuthenticated]
	);

	// Page API functions
	const createPage = useCallback(
		async (projectId, pageType, title) => {
			if (!projectId || !pageType || !title) {
				throw new Error("Missing required parameters: projectId, pageType, and title are required");
			}

			return api("/page", {
				method: "POST",
				body: {
					projectId,
					pageType,
					title,
				},
			});
		},
		[api]
	);

	const updatePage = useCallback(
		async (pageId, content) => {
			return api("/page", {
				method: "PUT",
				body: {
					pageId,
					content,
				},
			});
		},
		[api]
	);

	const getPages = useCallback(
		async (projectId) => {
			return api(`/canvas?projectId=${projectId}`, {
				method: "GET",
			});
		},
		[api]
	);

	const deletePage = useCallback(
		async (pageId, projectId) => {
			return api("/page", {
				method: "DELETE",
				body: {
					pageId,
					projectId,
				},
			});
		},
		[api]
	);

	const updateCanvas = useCallback(
		async (projectId, content) => {
			if (!projectId) {
				throw new Error("Missing required parameter: projectId");
			}

			return api("/canvas", {
				method: "PUT",
				body: {
					projectId,
					content,
					action: content.action || "save",
				},
			});
		},
		[api]
	);

	return (
		<ApiContext.Provider
			value={{
				api,
				// Page functions
				createPage,
				updatePage,
				getPages,
				deletePage,
				updateCanvas,
			}}>
			{children}
		</ApiContext.Provider>
	);
};

export const useApi = () => {
	const context = useContext(ApiContext);
	if (context === undefined) {
		throw new Error("useApi must be used within an ApiProvider");
	}
	return context;
};

export default ApiContext;
